import logger from '$lib/logger';
import type {
  NewRoastEvent,
  RoastChartData,
  StarredRoast,
  StarredRoastEvent,
  TimeTempPair
} from '$lib/types/RoastLog';

import * as d3 from 'd3';

import { getXAxisBoundsAndTicks, getYAxisBounds } from './chartUtils';

type PlotData = {
  label: string;
  data: TimeTempPair[] | null;
};

type SVGChart = d3.Selection<SVGGElement, unknown, HTMLElement, any>;

const timeFormatter = d3.timeFormat('%M:%S');
const DOT_RADIUS = 3;

const getFormattedTime = (d: NewRoastEvent | StarredRoastEvent): string => {
  // Check if object has formattedTime property (only NewRoastEvent does)
  if ('formattedTime' in d && d.formattedTime) {
    return d.formattedTime;
  }
  // Otherwise convert time to string. In this case it should just be return d.time since it's a
  // StarredRoastEvent that has a string value for time. However, this will keep TS happy.
  return d.time.toString();
};

const timeToSeconds = (timeStr: string) => {
  const values = timeStr.split(':').map((i) => parseInt(i));
  return values[0] * 60 + values[1];
};

export const PlotRoastProfile = async (
  element: string,
  roast: StarredRoast,
  display: 'dots' | 'labels'
) => {
  const chart = new RoastProfileChart();
  await chart.draw(element, roast, display);
  return chart;
};

export const DrawEmptyProfile = async () => {
  const chart = new RoastProfileChart();
  await chart.drawEmpty();
  return chart;
};

const xGrid = (
  g: d3.Selection<SVGGElement, unknown, HTMLElement, any>,
  xAxis: d3.Axis<d3.AxisDomain>,
  xScale: (arg0: any) => any,
  height: number
) =>
  g
    .selectAll('line')
    .data(xAxis.tickValues()!)
    .join('line')
    .attr('x1', (d) => xScale(d))
    .attr('y1', 0)
    .attr('x2', (d) => xScale(d))
    .attr('y2', height)
    .style('stroke', '#c8c8c8')
    .style('stroke-width', 0.5)
    .style('stroke-dasharray', '3 3');

const yGrid = (g: any, yScale: any, width: number) =>
  g
    .selectAll('line')
    .data(yScale.ticks())
    .join('line')
    .attr('x1', 0)
    .attr('x2', width)
    .attr('y1', (d: any) => yScale(d))
    .attr('y2', (d: any) => yScale(d))
    .style('stroke', '#c8c8c8')
    .style('stroke-width', 0.5)
    .style('stroke-dasharray', '3 3');

export class RoastProfileChart {
  margin: {
    top: number;
    right: number;
    bottom: number;
    left: number;
  };
  colors!: d3.ScaleOrdinal<string, string, never>;
  // backgroundProfiles: { label: string; data: TimeTempPair[] }[] = [];
  backgroundProfiles: PlotData[] = [];
  backgroundProfilesY2!: PlotData[];
  // backgroundProfilesY2!: { label: string; data: TimeTempPair[] }[];
  realtimeProfiles!: { label: string; data: TimeTempPair[] }[];
  // realtimeData: TimeTempPair[];

  svg!: d3.Selection<SVGGElement, unknown, HTMLElement, any>;
  viewboxWidth!: number;
  viewboxHeight!: number;
  width!: number;
  height!: number;
  xAxis!: d3.Axis<d3.AxisDomain>;
  yAxisLeft!: d3.Axis<d3.AxisDomain>;
  yAxisRight!: d3.Axis<d3.AxisDomain>;

  xAxisElement!: d3.Selection<SVGGElement, unknown, HTMLElement, any>;
  yAxisElement!: d3.Selection<SVGGElement, unknown, HTMLElement, any>;

  line!: d3.Line<[number, number]>;
  realtimeLine!: d3.Line<[number, number]>;
  realtimeLines!: d3.Selection<
    SVGPathElement,
    { label: string; data: TimeTempPair[] },
    SVGGElement,
    unknown
  >;
  heatingRateLine!: d3.Line<[number, number]>;

  xMax: number = 0;

  yMin!: number;
  yMax!: number;

  xScale: any;
  yScale: any;
  y2Scale: any;
  xGrid!: d3.Selection<SVGGElement, unknown, HTMLElement, any>;
  yGridElement: any;
  backgrounLines: d3.Selection<SVGGElement, PlotData, SVGGElement, unknown> | undefined;
  heatingRateBackgroundLine: d3.Selection<SVGGElement, PlotData, SVGGElement, unknown> | undefined;

  heatingRateProfile: { label: string; data: TimeTempPair[] }[];
  heatingRateLines:
    | d3.Selection<SVGPathElement, { label: string; data: TimeTempPair[] }, SVGGElement, unknown>
    | undefined;
  labels = ['Bean', 'TC2', 'TC3', 'TC4', 'Heating Rate'];
  xGridElement!: d3.Selection<SVGGElement, unknown, HTMLElement, any>;
  eventInfoBox: d3.Selection<HTMLDivElement, unknown, HTMLElement, any>;

  isDarkMode: boolean | undefined;

  constructor() {
    this.margin = {
      top: 10,
      right: 20,
      bottom: 20,
      left: 30
    };

    this.isDarkMode = document.querySelector('html')!.classList.contains('dark');

    // The labels below are what align the colors
    this.realtimeProfiles = [
      { label: 'Bean', data: [] },
      { label: 'TC2', data: [] },
      { label: 'TC3', data: [] },
      { label: 'TC4', data: [] }
    ];
    this.heatingRateProfile = [{ label: 'Heating Rate', data: [] }];

    this.colors = d3.scaleOrdinal(d3.schemeCategory10).domain(this.labels);

    this.eventInfoBox = d3
      .select('body')
      .append('div')
      .attr('class', 'p-2 rounded-lg bg-card text-card-foreground drop-shadow-sm')
      .style('position', 'absolute')
      .style('visibility', 'hidden');

    // Bind the class to these callbacks so that they have access to class properties
    this.handleMouseOver = this.handleMouseOver.bind(this);
    this.handleMouseLeave = this.handleMouseLeave.bind(this);
  }

  async draw(chartId: string, roast: StarredRoast, display: 'dots' | 'labels'): Promise<boolean> {
    const chartReady = await this.setChartDimensions(chartId);
    if (chartReady === false) {
      return false;
    }

    const profiles: PlotData[] = this.labels.map((label, i) => ({
      label,
      data: roast.profile.plots[i] ?? null
    }));

    this.backgroundProfiles = profiles
      .filter((line) => line.data !== null && line.label !== 'Heating Rate')
      .reverse(); // reverse these so the bean line is always on top

    this.backgroundProfilesY2 = profiles.filter(
      (line, i) => (i === 3 || line.label === 'Heating Rate') && line.data !== null
    );

    const svg = this.initChart();

    this.setAxesBounds(this.backgroundProfiles);
    this.drawAxes(svg);

    this.drawBackgroundLines(svg);
    this.initRealtimeProfiles(svg);
    this.initHeatingRateProfile(svg);

    // const eventPositions = roast.profile.events.map((d) => this.xScale(timeToSeconds(d.time)));
    // this.addScrubber(this.svg, eventPositions, data.events);

    if (display === 'labels') {
      this.drawBackgroundEventLabels(svg, roast.profile.events);
    } else {
      this.drawBackgroundEventDots(svg, roast.profile.events);
    }

    this.svg = svg;
    return true;
  }

  async drawEmpty(): Promise<boolean> {
    const chartReady = await this.setChartDimensions('chart');
    if (chartReady === false) {
      return false;
    }

    // hard code the data
    this.backgroundProfiles = [
      {
        label: 'Bean',
        data: [
          [0, 100],
          [60, 250]
        ]
      }
    ];
    this.backgroundProfilesY2 = [];

    const svg = this.initChart();

    this.setAxesBounds(this.backgroundProfiles);
    this.drawAxes(svg);
    this.initRealtimeProfiles(svg);
    this.initHeatingRateProfile(svg);

    this.svg = svg;
    return true;
  }

  initChart(): SVGChart {
    d3.select('#chart svg').remove();

    const svg = d3
      .select('#chart')
      .append('svg')
      .attr('viewBox', `0 0 ${this.viewboxWidth} ${this.viewboxHeight}`)
      .append('g')
      .attr('transform', `translate(${this.margin.left}, ${this.margin.top})`);

    svg
      .append('clipPath')
      .attr('id', 'clip-path')
      .append('rect')
      .attr('width', this.width)
      .attr('height', this.height);

    // scales
    this.xScale = d3.scaleTime().range([0, this.width]);
    this.yScale = d3.scaleLinear().range([this.height, 0]);
    this.y2Scale = d3.scaleLinear().domain([0, 50]).range([this.height, 0]).nice();

    // axes
    this.xAxis = d3
      .axisBottom(this.xScale)
      .tickFormat((t: any) => timeFormatter(new Date(t * 1000)));

    this.yAxisLeft = d3.axisLeft(this.yScale);
    this.yAxisRight = d3.axisRight(this.y2Scale);

    // main lines
    this.line = d3
      .line()
      .x((d) => this.xScale(d[0]))
      .y((d) => this.yScale(d[1]))
      .curve(d3.curveBasis);

    this.realtimeLine = d3
      .line()
      .x((d) => this.xScale(d[0]))
      .y((d) => this.yScale(d[1]))
      .curve(d3.curveBasis);

    this.heatingRateLine = d3
      .line()
      .x((d) => this.xScale(d[0]))
      .y((d) => this.y2Scale(d[1]))
      .curve(d3.curveBasis);

    return svg;
  }

  private async setChartDimensions(chartId: string, retryCount: number = 0): Promise<boolean> {
    const chartContainer = document.getElementById(chartId);
    logger.debug('Setting chart dimensions', { chartContainer, retryCount });

    if (!chartContainer || !chartContainer.parentElement) {
      logger.warn(`Chart container not found in draw: ${chartId}`);
      return false;
    }

    this.viewboxWidth = chartContainer.offsetWidth;
    this.viewboxHeight = chartContainer.offsetHeight;

    // Validate dimensions before calculating margins
    if (this.viewboxWidth <= 0 || this.viewboxHeight <= 0) {
      logger.warn('Chart container has invalid dimensions, retrying...', {
        viewboxWidth: this.viewboxWidth,
        viewboxHeight: this.viewboxHeight,
        retryCount
      });

      // Retry up to 3 times using requestAnimationFrame
      if (retryCount < 3) {
        return new Promise((resolve) => {
          requestAnimationFrame(async () => {
            const result = await this.setChartDimensions(chartId, retryCount + 1);
            resolve(result);
          });
        });
      } else {
        logger.error('Failed to get valid chart dimensions after 3 retries', {
          viewboxWidth: this.viewboxWidth,
          viewboxHeight: this.viewboxHeight
        });
        return false;
      }
    }

    this.width = this.viewboxWidth - this.margin.left - this.margin.right;
    this.height = this.viewboxHeight - this.margin.top - this.margin.bottom;

    // Final validation of calculated dimensions
    if (this.width <= 0 || this.height <= 0) {
      logger.error('Calculated chart dimensions are invalid', {
        viewboxWidth: this.viewboxWidth,
        viewboxHeight: this.viewboxHeight,
        width: this.width,
        height: this.height,
        margins: this.margin
      });
      return false;
    }

    logger.debug('Chart dimensions set successfully', {
      viewboxWidth: this.viewboxWidth,
      viewboxHeight: this.viewboxHeight,
      width: this.width,
      height: this.height,
      retryCount
    });
    return true;
  }

  drawAxes(svg: SVGChart) {
    // Add the axes for x and y
    this.xAxisElement = svg
      .append('g')
      .attr('class', 'x-axis')
      .attr('transform', `translate(0, ${this.height})`)
      .call(this.xAxis);
    this.yAxisElement = svg.append('g').attr('class', 'y-axis').call(this.yAxisLeft);

    // Build the horizontal and vertical grids
    this.xGridElement = svg.append('g').attr('class', 'vertical-grid');
    this.xGridElement.call((g: any) => xGrid(g, this.xAxis, this.xScale, this.height));

    this.yGridElement = svg.append('g').attr('class', 'horizontal-grid');
    this.yGridElement.call((g: any) => yGrid(g, this.yScale, this.width));

    if (this.backgroundProfilesY2.length > 0) {
      svg
        .append('g')
        .attr('class', 'y2-axis')
        .attr('transform', `translate(${this.width}, 0)`)
        .call(this.yAxisRight);
    }
  }

  /**
   * The reference background lines, both the temperatures and heating rate.
   */
  drawBackgroundLines(svg: SVGChart) {
    this.backgrounLines = svg
      .selectAll('.bgprofile')
      .data(this.backgroundProfiles)
      .enter()
      .append('g')
      .attr('class', 'profile bgprofile');

    this.backgrounLines
      .append('path')
      .attr('class', 'line')
      .attr('d', (d: PlotData) => this.line(d.data!))
      .style('stroke', (d, i) => this.colors(d.label))
      .style('opacity', this.isDarkMode ? 0.3 : 0.2)
      .style('stroke-width', 2);

    if (this.backgroundProfilesY2.length > 0 && this.backgroundProfilesY2[0].data) {
      this.heatingRateBackgroundLine = svg
        .selectAll('.hrprofile')
        .data(this.backgroundProfilesY2)
        .enter()
        .append('g')
        .attr('class', 'profile hrprofile');

      this.heatingRateBackgroundLine
        .append('path')
        .attr('class', 'line')
        .attr('clip-path', 'url(#clip-path)')
        .attr('d', (d: PlotData) => this.heatingRateLine(d.data!))
        .style('stroke', this.isDarkMode ? '#FFF' : '#000')
        .style('opacity', this.isDarkMode ? 0.3 : 0.2)
        .style('stroke-width', 1);
    }
  }

  /**
   * Real-time heating rate line
   */
  private initHeatingRateProfile(svg: SVGChart) {
    this.heatingRateLines = svg
      .selectAll('.heating-rate')
      .data(this.heatingRateProfile)
      .enter()
      .append('g')
      .attr('class', 'profile heating-rate')
      .append('path')
      .attr('class', 'hr-line')
      .attr('clip-path', 'url(#clip-path)')
      .style('stroke', this.isDarkMode ? '#FFF' : '#000')
      .style('stroke-width', 1);
  }

  /**
   *  Real-time lines
   */
  private initRealtimeProfiles(svg: SVGChart) {
    this.realtimeLines = svg
      .selectAll('.realtime-profile')
      .data(this.realtimeProfiles)
      .enter()
      .append('g')
      .attr('class', 'profile realtime-profile')
      .append('path')
      .attr('class', 'realtime-line')
      .attr('clip-path', 'url(#clip-path)')
      .style('stroke-width', 2)
      .style('stroke', (d) => this.colors(d.label));
  }

  update(data: RoastChartData) {
    // const [tc1Data, tc2Data, tc3Data, tc4Data, hrData] = data;
    const tc1Data = data[0];
    const tc2Data = data[1];
    const tc3Data = data[2];
    const tc4Data = data[3];
    const hrData = data[4];

    // Safety check...this should never happen
    if (tc1Data.length < 1) return;

    // bean temp
    this.realtimeProfiles[0].data = tc1Data;

    if (tc2Data) {
      this.realtimeProfiles[1].data = tc2Data;
    }
    if (tc3Data) {
      this.realtimeProfiles[2].data = tc3Data;
    }
    if (tc4Data) {
      this.realtimeProfiles[3].data = tc4Data;
    }

    if (hrData) {
      this.heatingRateProfile[0].data = hrData;
    }

    const redrawAxes = this.setAxesBounds(this.realtimeProfiles);

    if (redrawAxes) {
      this.xAxisElement.transition().duration(1000).call(this.xAxis);
      this.yAxisElement.transition().duration(1000).call(this.yAxisLeft);

      // transition/duration not working for the ygrid
      this.yGridElement.call((g: any) => yGrid(g, this.yScale, this.width));

      // update the background
      if (this.backgrounLines) {
        this.backgrounLines
          .select('path.line')
          .transition()
          .duration(1000)
          .attr('d', (d: PlotData) => this.line(d.data!));

        if (this.heatingRateBackgroundLine) {
          this.heatingRateBackgroundLine
            .select('path.line')
            .transition()
            .duration(1000)
            .attr('d', (d: PlotData) => this.heatingRateLine(d.data!));
        }

        // Update event dots
        const eventDots = this.svg.selectAll<SVGCircleElement, StarredRoastEvent | NewRoastEvent>(
          'circle'
        );
        if (eventDots) {
          eventDots
            .transition()
            .duration(1000)
            .attr('cx', (d) => this.xScale(timeToSeconds(getFormattedTime(d))))
            .attr('cy', (d) =>
              this.yScale(
                typeof d.temperature === 'number' ? d.temperature : parseFloat(d.temperature)
              )
            );
        }
      }
    }

    this.realtimeLines.attr('d', (d: PlotData) => this.realtimeLine(d.data!));
    this.heatingRateLines!.attr('d', (d: PlotData) => this.heatingRateLine(d.data!));
  }

  private handleMouseOver(event: MouseEvent, d: StarredRoastEvent | NewRoastEvent) {
    d3.select(event.currentTarget as Element)
      .transition()
      .attr('r', DOT_RADIUS * 2);

    const temperature =
      typeof d.temperature === 'number' ? d.temperature.toFixed(1) : d.temperature;

    this.eventInfoBox
      .style('left', event.pageX + 10 + 'px')
      .style('top', event.pageY - 10 + 'px')
      .html(
        `
      <div class="font-bold">${d.description}</div>
      <div>${temperature}° at ${getFormattedTime(d)}</div>
    `
      )
      .style('visibility', 'visible');
  }

  private handleMouseLeave(event: MouseEvent) {
    d3.select(event.currentTarget as Element)
      .transition()
      .attr('r', DOT_RADIUS);
    this.eventInfoBox.style('visibility', 'hidden');
  }

  updateEventDots(events: NewRoastEvent[]) {
    const eventDots = this.svg.selectAll('circle.realtime-event').data(events);

    eventDots
      .enter()
      .append('circle')
      .attr('class', 'realtime-event') // Add this class
      .attr('r', 0)
      .attr('cx', (d) => this.xScale(d.time))
      .attr('cy', (d) => this.yScale(d.temperature))
      .style('fill', (d: any) => this.colors('Bean'))
      .style('stroke', (d: any) => this.colors('Bean'))
      .attr('r', DOT_RADIUS)
      .style('opacity', 1.0)
      .on('mouseover', this.handleMouseOver)
      .on('mouseleave', this.handleMouseLeave)
      .transition();

    // Handle updates for existing foreground circles
    eventDots.attr('cx', (d) => this.xScale(d.time)).attr('cy', (d) => this.yScale(d.temperature));

    // Handle exit for removed foreground circles if needed
    eventDots.exit().remove();
  }

  private setAxesBounds(profiles: PlotData[]) {
    const updatedXBounds = this.setXAxisBoundsAndTicks(profiles);
    const updatedYBounds = this.setYAxisBounds(profiles);

    return updatedXBounds || updatedYBounds;
  }

  private setXAxisBoundsAndTicks(profiles: PlotData[]): boolean {
    const { redrawChart, ticks, xUpperBound } = getXAxisBoundsAndTicks(
      profiles[0].data!,
      this.xMax
    );

    if (xUpperBound > this.xMax) {
      this.xMax = xUpperBound;
    }

    if (redrawChart) {
      this.xScale.domain([0, xUpperBound]);
      this.xAxis.tickValues(ticks!);
      // Note that this function seems to produce the same result as the above. Even through we're
      // storing integers for time, calling the millisecond interval is necessary as the timeSecond
      // interval doesn't work.
      // this.xAxis.ticks(d3.timeMillisecond.every(60));
    }

    return redrawChart;
  }

  private setYAxisBounds(profiles: PlotData[]) {
    const data = profiles.map((p) => p.data);
    const yBounds = getYAxisBounds(data, 25);

    // First time we call this, the bounds won't be set at all
    if (!this.yMax) {
      [this.yMin, this.yMax] = yBounds;
      this.yScale.domain(yBounds);
      return true;
    }

    // Now, compare y min/max to see if the axis needs updating
    let updateBounds = false;
    if (yBounds[0] < this.yMin) {
      this.yMin = yBounds[0];
      updateBounds = true;
    }
    if (yBounds[1] > this.yMax) {
      this.yMax = yBounds[1];
      updateBounds = true;
    }

    if (updateBounds) {
      this.yScale.domain([this.yMin, this.yMax]);
    }

    return updateBounds;
  }

  private drawBackgroundEventDots(svg: SVGChart, events: StarredRoastEvent[]) {
    const eventDots = svg.selectAll('circle.reference-event').data(events);

    eventDots
      .enter()
      .append('circle')
      .attr('class', 'reference-event') // Add this class
      .attr('r', 0)
      .attr('cx', (d: { time: string }) => this.xScale(timeToSeconds(d.time)))
      .attr('cy', (d: { temperature: any }) => this.yScale(d.temperature))
      .style('fill', (d: any) => this.colors('Bean'))
      .style('stroke', (d: any) => this.colors('Bean'))
      .attr('r', DOT_RADIUS)
      .style('opacity', 0.5)
      .on('mouseover', this.handleMouseOver)
      .on('mouseleave', this.handleMouseLeave)
      .transition();
  }

  drawBackgroundEventLabels(svg: SVGChart, events: StarredRoastEvent[]) {
    // Remove any existing background event label lines and texts
    svg.selectAll('line.background-event-label').remove();
    svg.selectAll('text.background-event-label').remove();
    svg.selectAll('rect.first-crack-shade').remove();
    svg.selectAll('text.dtr-label').remove();

    // Find Start First Crack and Drop Temperature events
    const firstCrackEvent = events.find((e) => {
      const desc = e.description.toLowerCase();
      return desc === 'start first crack' || desc.startsWith('first crack');
    });
    const dropEvent = events.find((e) => e.description.toLowerCase() === 'drop temperature');

    // Draw shaded area if Start First Crack exists
    if (firstCrackEvent) {
      const startX = this.xScale(timeToSeconds(firstCrackEvent.time));
      const endX = dropEvent ? this.xScale(timeToSeconds(dropEvent.time)) : this.width;

      console.log(firstCrackEvent);

      svg
        .append('rect')
        .attr('class', 'first-crack-shade')
        .attr('x', startX)
        .attr('y', 0)
        .attr('width', endX - startX)
        .attr('height', this.height)
        .style('fill', this.isDarkMode ? '#FFF' : this.colors('Bean'))
        .style('opacity', 0.05)
        .lower();

      // Add DTR label in the middle of the shaded area
      const dtrPercentage = (100 - parseFloat(firstCrackEvent.percentage)).toFixed(1);
      svg
        .append('text')
        .attr('class', 'dtr-label')
        .attr('x', (startX + endX) / 2)
        .attr('y', this.height / 2)
        .text(`DTR: ${dtrPercentage}%`)
        .style('font-size', '14px')
        .style('font-weight', 'bold')
        .style('fill', this.colors('Bean'))
        .style('text-anchor', 'middle')
        .style('alignment-baseline', 'middle');
    }

    // Sort events by x position (time)
    const eventsWithX = events.map((d) => ({ ...d, x: this.xScale(timeToSeconds(d.time)) }));
    eventsWithX.sort((a, b) => a.x - b.x);

    // Layout: stack labels vertically if they would overlap
    const labelYStart = 12;
    const labelYStep = 16;
    const occupiedPositions: { x: number; y: number; width: number }[] = [];

    eventsWithX.forEach((d, i) => {
      const x = d.x;
      const label = `${getFormattedTime(d)} - ${d.description} - ${d.temperature.toFixed(1)}\u00B0`;
      const labelWidth = label.length * 6.0; // Approximate character width
      const isLastEvent = i === eventsWithX.length - 1;
      const labelX = isLastEvent ? x - labelWidth + 15 : x + 2;
      let labelY = labelYStart;

      // Find the first available Y position
      while (
        occupiedPositions.some(
          (pos) => pos.y === labelY && labelX < pos.x + pos.width && labelX + labelWidth > pos.x
        )
      ) {
        labelY += labelYStep;
      }

      occupiedPositions.push({ x: labelX, y: labelY, width: labelWidth });

      // Draw vertical line
      if (d.description.toLowerCase() !== 'intro temperature') {
        svg
          .append('line')
          .attr('class', 'background-event-label')
          .attr('x1', x)
          .attr('x2', x)
          .attr('y1', 0)
          .attr('y2', this.height)
          .style('stroke', this.isDarkMode ? '#FFF' : this.colors('Bean'))
          .style('stroke-width', 1)
          .style('opacity', this.isDarkMode ? 0.75 : 0.5)
          .lower();
      }

      // Draw label (time - event - temperature)
      svg
        .append('text')
        .attr('class', 'background-event-label')
        .attr('x', labelX)
        .attr('y', labelY)
        .text(label)
        .style('font-size', '11px')
        .style('fill', this.isDarkMode ? '#FFF' : '#000')
        .style('alignment-baseline', 'hanging')
        .style('pointer-events', 'auto')
        .on('mouseover', function () {
          d3.select(this).style('font-weight', 'bold').style('font-size', '14px');
        })
        .on('mouseleave', function () {
          d3.select(this).style('font-weight', null).style('font-size', '11px');
        });
    });
  }

  clearChart() {
    this.svg.selectAll('circle').remove();
    this.svg.selectAll('path.line').remove();
    this.svg.selectAll('.profile').remove();
  }
}

export const roastChart = new RoastProfileChart();
